import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FolderOpen, HouseLine, Baby } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import {
  listDocumentTypes,
  listFamilyDocuments,
  listChildDocuments,
  deleteFamilyDocument,
  deleteChildDocument,
  signDocument,
  buildDocumentChecklistRows,
  summariseDocumentChecklist,
} from "@/lib/documents";
import {
  uploadFamilyDocument,
  uploadChildDocument,
  familyDocumentSignedUrl,
  childDocumentSignedUrl,
} from "@/lib/storage";
import DocumentRequirementRow from "@/components/documents/DocumentRequirementRow";

/**
 * Parent · /parent/documents
 *
 * Shows every required + optional document for the parent's family + each
 * of their children, with inline upload / sign / re-upload / re-sign.
 */
export default function ParentDocuments() {
  const { profile } = useAuth();
  const [searchParams] = useSearchParams();
  const focus = searchParams.get("focus"); // missing | expiring | rejected | uploaded | expired
  const [loading, setLoading] = useState(true);
  const [family, setFamily] = useState(null); // { id, organization_id, name }
  const [children, setChildren] = useState([]);
  const [familyTypes, setFamilyTypes] = useState([]);
  const [childTypes, setChildTypes] = useState([]);
  const [familyDocs, setFamilyDocs] = useState([]);
  const [childDocsByChildId, setChildDocsByChildId] = useState({});

  useEffect(() => {
    if (!profile?.id) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        // 1. Resolve the parent's family + children.
        const { data: memberships, error: mErr } = await supabase
          .from("family_members")
          .select("family_id, families:families!inner(id, name:display_name, organization_id)")
          .eq("profile_id", profile.id)
          .limit(1);
        if (mErr) throw mErr;
        const fam = memberships?.[0]?.families;
        if (!fam) {
          if (alive) {
            setFamily(null);
            setChildren([]);
            setLoading(false);
          }
          return;
        }
        const { data: kids, error: cErr } = await supabase
          .from("children")
          .select("id, first_name, last_name, organization_id, family_id")
          .eq("family_id", fam.id)
          .order("first_name");
        if (cErr) throw cErr;

        // 2. Load the type definitions once.
        const [fTypes, cTypes] = await Promise.all([
          listDocumentTypes({ organizationId: fam.organization_id, scope: "family" }),
          listDocumentTypes({ organizationId: fam.organization_id, scope: "child" }),
        ]);

        // 3. Load docs.
        const fDocs = await listFamilyDocuments(fam.id);
        const docsByChild = {};
        for (const kid of kids || []) {
          // eslint-disable-next-line no-await-in-loop
          docsByChild[kid.id] = await listChildDocuments(kid.id);
        }

        if (!alive) return;
        setFamily(fam);
        setChildren(kids || []);
        setFamilyTypes(fTypes);
        setChildTypes(cTypes);
        setFamilyDocs(fDocs);
        setChildDocsByChildId(docsByChild);
      } catch (err) {
        toast.error(err.message || "Could not load documents");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [profile?.id]);

  const familyRows = useMemo(
    () => buildDocumentChecklistRows(familyTypes, familyDocs),
    [familyTypes, familyDocs],
  );
  const allDocumentRows = useMemo(
    () => [
      ...familyRows,
      ...children.flatMap((child) => buildDocumentChecklistRows(
        childTypes,
        childDocsByChildId[child.id] || [],
      )),
    ],
    [familyRows, children, childTypes, childDocsByChildId],
  );
  const complianceSummary = useMemo(
    () => summariseDocumentChecklist(allDocumentRows),
    [allDocumentRows],
  );

  async function refreshFamilyDocs() {
    if (!family) return;
    const docs = await listFamilyDocuments(family.id);
    setFamilyDocs(docs);
  }
  async function refreshChildDocs(childId) {
    const docs = await listChildDocuments(childId);
    setChildDocsByChildId((prev) => ({ ...prev, [childId]: docs }));
  }

  // When the compliance card deep-links with ?focus=…, wait for paint, then
  // scroll the first row whose data-status / data-expiry matches into view.
  useEffect(() => {
    if (loading || !focus) return;
    const id = window.setTimeout(() => {
      const root = document.querySelector('[data-testid="parent-documents"]');
      if (!root) return;
      const selector =
        focus === "expiring" || focus === "expired"
          ? `[data-expiry="${focus}"]`
          : `[data-status="${focus}"]`;
      const target = root.querySelector(selector);
      if (target && typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        target.setAttribute("data-focused", "true");
      }
    }, 200);
    return () => window.clearTimeout(id);
  }, [loading, focus, familyDocs, childDocsByChildId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  if (!family) {
    return (
      <div data-testid="parent-documents-empty" className="rounded-2xl border border-neutral-200 bg-white p-8 text-center">
        <FolderOpen size={28} weight="duotone" className="mx-auto text-neutral-400" />
        <p className="mt-3 text-sm text-neutral-600">
          Your account isn't linked to a family yet. Contact your center's admin.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="parent-documents" className="space-y-8">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Account
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Documents & forms
        </h1>
        <p className="mt-2 max-w-2xl text-neutral-600">
          Upload or sign the paperwork your center needs on file. Some items
          expire annually — we'll flag them ahead of time so nothing lapses.
        </p>
      </header>

      {/* Readiness strip */}
      <section
        data-testid="parent-documents-readiness"
        className="flex flex-wrap items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-4"
      >
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Completion across family & children
        </span>
        <div className="flex-1">
          <div className="h-2 overflow-hidden rounded-full bg-neutral-100">
            <div
              data-testid="parent-documents-progress-bar"
              className="h-full rounded-full bg-primary"
              style={{ width: `${complianceSummary.percentComplete}%` }}
            />
          </div>
        </div>
        <span data-testid="parent-documents-percent" className="text-sm font-semibold text-neutral-900">
          {complianceSummary.percentComplete}%
        </span>
      </section>

      {/* Family-scope requirements */}
      <section>
        <div className="mb-4 flex items-center gap-2">
          <HouseLine size={18} weight="duotone" className="text-primary-hover" />
          <h2 className="font-heading text-lg font-semibold text-neutral-900">
            Family documents
          </h2>
        </div>
        <div data-testid="parent-family-docs" className="space-y-4">
          {familyRows.map((row) => (
            <DocumentRequirementRow
              key={row.type.id}
              row={row}
              role="parent"
              onUpload={async (file) => {
                await uploadFamilyDocument({
                  familyId: family.id,
                  organizationId: family.organization_id,
                  documentTypeId: row.type.id === "__other__" ? null : row.type.id,
                  file,
                  uploadedBy: profile.id,
                });
                await refreshFamilyDocs();
              }}
              onSign={async (signedName) => {
                await signDocument({
                  scope: "family",
                  recordId: family.id,
                  documentTypeId: row.type.id,
                  signedName,
                });
                await refreshFamilyDocs();
              }}
              onDelete={async (doc) => {
                await deleteFamilyDocument({ id: doc.id, storagePath: doc.storage_path });
                await refreshFamilyDocs();
              }}
              onDownload={async (doc) =>
                doc.storage_path ? familyDocumentSignedUrl(doc.storage_path) : null
              }
            />
          ))}
          {familyRows.length === 0 && (
            <p className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 p-6 text-center text-sm text-neutral-500">
              No family documents configured for your center yet.
            </p>
          )}
        </div>
      </section>

      {/* Child-scope requirements */}
      {children.map((kid) => (
        <ChildDocsSection
          key={kid.id}
          child={kid}
          types={childTypes}
          docs={childDocsByChildId[kid.id] || []}
          profileId={profile.id}
          onRefresh={() => refreshChildDocs(kid.id)}
        />
      ))}
    </div>
  );
}

function ChildDocsSection({ child, types, docs, profileId, onRefresh }) {
  const rows = useMemo(() => buildDocumentChecklistRows(types, docs), [types, docs]);
  const summary = useMemo(() => summariseDocumentChecklist(rows), [rows]);
  const fullName = `${child.first_name} ${child.last_name || ""}`.trim();

  return (
    <section data-testid={`parent-child-docs-${child.id}`}>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Baby size={18} weight="duotone" className="text-primary-hover" />
          <h2 className="font-heading text-lg font-semibold text-neutral-900">
            {fullName}'s documents
          </h2>
        </div>
        <span
          data-testid={`parent-child-docs-${child.id}-percent`}
          className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-semibold text-neutral-700"
        >
          {summary.percentComplete}% complete
        </span>
      </div>
      <div className="space-y-4">
        {rows.map((row) => (
          <DocumentRequirementRow
            key={row.type.id}
            row={row}
            role="parent"
            onUpload={async (file) => {
              await uploadChildDocument({
                childId: child.id,
                organizationId: child.organization_id,
                documentTypeId: row.type.id === "__other__" ? null : row.type.id,
                file,
                uploadedBy: profileId,
              });
              await onRefresh();
            }}
            onSign={async (signedName) => {
              await signDocument({
                scope: "child",
                recordId: child.id,
                documentTypeId: row.type.id,
                signedName,
              });
              await onRefresh();
            }}
            onDelete={async (doc) => {
              await deleteChildDocument({ id: doc.id, storagePath: doc.storage_path });
              await onRefresh();
            }}
            onDownload={async (doc) =>
              doc.storage_path ? childDocumentSignedUrl(doc.storage_path) : null
            }
          />
        ))}
        {rows.length === 0 && (
          <p className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 p-6 text-center text-sm text-neutral-500">
            No child documents configured for your center yet.
          </p>
        )}
      </div>
    </section>
  );
}
